/**
 * An image committed to a repository and linked by its GitHub file URL, such as
 * `https://github.com/{owner}/{repo}/blob/{ref}/{path}?raw=true`. In a private repository GitHub
 * serves these only to a browser signed in to github.com, so on cubic they load through
 * GITHUB_REPO_IMAGE_ROUTE, which reads the file with the viewer's own GitHub access.
 */
export type GithubRepoImage = {
  owner: string;
  repo: string;
  /** The ref and the file path, split on `/`. A branch name can contain `/`, so the URL alone cannot tell where the ref ends. */
  refAndPath: string[];
};

export const GITHUB_REPO_IMAGE_ROUTE = '/api/github/repo';

const IMAGE_CONTENT_TYPES = new Map([
  ['avif', 'image/avif'],
  ['bmp', 'image/bmp'],
  ['gif', 'image/gif'],
  ['ico', 'image/x-icon'],
  ['jpeg', 'image/jpeg'],
  ['jpg', 'image/jpeg'],
  ['png', 'image/png'],
  ['svg', 'image/svg+xml'],
  ['webp', 'image/webp'],
]);

const OWNER_PATTERN = /^[A-Za-z\d-]{1,39}$/;
const REPO_PATTERN = /^[\w.-]{1,100}$/;

function isSafeSegment(segment: string): boolean {
  return segment !== '' && segment !== '.' && segment !== '..' && !/[/\\]/.test(segment);
}

export function getGithubRepoImageContentType(fileName: string): string | null {
  const extension = /\.(\w+)$/.exec(fileName)?.[1]?.toLowerCase();
  return extension ? (IMAGE_CONTENT_TYPES.get(extension) ?? null) : null;
}

/** Validates decoded URL parts; the route gets the same parts from its own URL. */
export function toGithubRepoImage(
  owner: string,
  repo: string,
  refAndPath: string[],
): GithubRepoImage | null {
  const fileName = refAndPath.at(-1);
  if (
    !OWNER_PATTERN.test(owner) ||
    !REPO_PATTERN.test(repo) ||
    !isSafeSegment(repo) ||
    refAndPath.length < 2 ||
    !refAndPath.every(isSafeSegment) ||
    !fileName ||
    !getGithubRepoImageContentType(fileName)
  ) {
    return null;
  }

  return { owner, repo, refAndPath };
}

function decodeSegments(segments: string[]): string[] | null {
  try {
    return segments.map((segment) => decodeURIComponent(segment));
  } catch {
    return null;
  }
}

/**
 * Reads `github.com/{owner}/{repo}/blob|raw/{ref}/{path}` and
 * `raw.githubusercontent.com/{owner}/{repo}/{ref}/{path}` image URLs.
 */
export function parseGithubRepoImageUrl(src: string): GithubRepoImage | null {
  let url: URL;
  try {
    url = new URL(src);
  } catch {
    return null;
  }

  if (url.protocol !== 'https:') {
    return null;
  }

  const segments = decodeSegments(url.pathname.split('/').slice(1));
  if (!segments) {
    return null;
  }

  if (url.hostname === 'github.com') {
    const [owner, repo, kind, ...refAndPath] = segments;
    if (!owner || !repo || (kind !== 'blob' && kind !== 'raw')) {
      return null;
    }
    return toGithubRepoImage(owner, repo, refAndPath);
  }

  if (url.hostname === 'raw.githubusercontent.com') {
    const [owner, repo, ...refAndPath] = segments;
    if (!owner || !repo) {
      return null;
    }
    return toGithubRepoImage(owner, repo, refAndPath);
  }

  return null;
}

function encodePath(segments: string[]): string {
  return segments.map((segment) => encodeURIComponent(segment)).join('/');
}

/** The cubic URL that serves a repository image, or null when `src` is not one. */
export function getGithubRepoImageProxyUrl(src: string): string | null {
  const image = parseGithubRepoImageUrl(src);
  if (!image) {
    return null;
  }

  return `${GITHUB_REPO_IMAGE_ROUTE}/${encodePath([image.owner, image.repo])}/raw/${encodePath(image.refAndPath)}`;
}

/** GitHub's own URL for the image, which works wherever the viewer's browser can read the repository. */
export function getGithubRepoImageUrl(image: GithubRepoImage): string {
  return `https://github.com/${encodePath([image.owner, image.repo])}/raw/${encodePath(image.refAndPath)}`;
}
